import React, { useRef, useState, useCallback } from "react";
import { Button } from "antd";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import "swiper/swiper.css";

const AUTOPLAY_DELAY = 4200;

const REGISTER_IMAGES = [
  { src: "/sections/register/frameless/01_Sopran.png",              label: "Sopransaxophon" },
  { src: "/sections/register/frameless/02_Alt1.png",                label: "Altsaxophon I" },
  { src: "/sections/register/frameless/03_Alt2.png",                label: "Altsaxophon II" },
  { src: "/sections/register/frameless/04_Tenor.png",               label: "Tenorsaxophon" },
  { src: "/sections/register/frameless/05_Bass.png",                label: "Baritonsaxophon & Bass" },
  { src: "/sections/register/frameless/06_Musikalische-Leitung.png", label: "Musikalische Leitung" },
];

function AppPlayers() {
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [titleRef, titleInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [recruitRef, recruitInView] = useInView({ triggerOnce: true, threshold: 0.15 });

  const handleSlideChange = useCallback((swiper) => {
    setActiveIndex(swiper.realIndex);
  }, []);

  return (
    <div id="players" className="playersBlock">
      <div className="container-fluid">
        <motion.div
          className="titleHolder"
          ref={titleRef}
          initial={{ opacity: 0, y: 20 }}
          animate={titleInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          <h2>Die Spielenden</h2>
        </motion.div>

        <p className="sectionIntro">
          Unser Ensemble setzt sich aus den fünf verschiedenen Stimmen zusammen – vom strahlenden Sopran
          über die warmen Töne der beiden Altsaxophon-Stimmen, die reiche Klangfarbe des Tenorregisters
          bis hin zum kraftvollen Bass, der durch Baritonsaxophon und Bassklarinette repräsentiert wird.
          Durch diese Besetzung und Vielzahl entstehen unsere wundervollen Klänge.
        </p>

        <motion.div
          className="swiperContainer"
          initial={{ opacity: 0, y: 30 }}
          animate={titleInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Swiper
            onSwiper={(s) => { swiperRef.current = s; }}
            modules={[Autoplay]}
            spaceBetween={24}
            slidesPerView={1}
            centeredSlides={true}
            autoplay={{ delay: AUTOPLAY_DELAY, disableOnInteraction: false, pauseOnMouseEnter: true }}
            loop={true}
            breakpoints={{
              768:  { slidesPerView: 1.3, spaceBetween: 30 },
              1024: { slidesPerView: 1.5, spaceBetween: 36 },
            }}
            onSlideChange={handleSlideChange}
          >
            {REGISTER_IMAGES.map((item) => (
              <SwiperSlide key={item.label}>
                <div className="playerSlide">
                  <img src={item.src} alt={item.label} loading="lazy" />
                  <div className="slideLabel">{item.label}</div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Custom Navigation + Progress Dots */}
          <div className="swiperControls">
            <button
              className="swiperNavBtn"
              onClick={() => swiperRef.current?.slidePrev()}
              aria-label="Vorherige Stimme"
            >
              <FaChevronLeft size={18} />
            </button>

            <div className="swiperDots">
              {REGISTER_IMAGES.map((item, i) => (
                <button
                  key={item.label}
                  className={`swiperDot ${i === activeIndex ? "swiperDotActive" : ""}`}
                  onClick={() => {
                    swiperRef.current?.slideToLoop(i);
                    setActiveIndex(i);
                  }}
                  aria-label={`Zu ${item.label}`}
                  title={item.label}
                >
                  {/* key changes on every activeIndex transition → React remounts the
                      span → CSS animation restarts cleanly from 0% each time */}
                  <span
                    key={i === activeIndex ? `f-active-${activeIndex}` : `f-${i}`}
                    className={`swiperDotFill${i === activeIndex ? " swiperDotFillActive" : ""}`}
                    style={{ width: i < activeIndex ? "100%" : "0%" }}
                  />
                </button>
              ))}
            </div>

            <button
              className="swiperNavBtn"
              onClick={() => swiperRef.current?.slideNext()}
              aria-label="Nächste Stimme"
            >
              <FaChevronRight size={18} />
            </button>
          </div>
        </motion.div>

        {/* WIR SUCHEN DICH */}
        <motion.div
          id="join"
          className="recruitBlock"
          ref={recruitRef}
          initial={{ opacity: 0, y: 25 }}
          animate={recruitInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55 }}
        >
          <h3>Wir suchen dich!</h3>
          <p>
            Du spielst Saxophon oder Bassklarinette und hast Lust mitzuspielen?
            Melde dich gerne über das Kontaktformular oder per Mail unter{" "}
            <a href="mailto:info@saxemble.de">info@saxemble.de</a> und werde Teil von uns.
            Wir freuen uns auf dich!
          </p>
          <Button
            type="primary"
            size="large"
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
          >
            Jetzt Kontakt aufnehmen
          </Button>
        </motion.div>
      </div>
    </div>
  );
}

export default AppPlayers;
